import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
  Spinner,
  Center,
  Heading,
  Text,
  Flex,
  IconButton,
  Grid,
  GridItem,
  Badge,
} from '@chakra-ui/react';
import { CheckIcon, EditIcon, CloseIcon } from '@chakra-ui/icons';
import { Helmet } from 'react-helmet-async';
import { secureFetch } from '../data/secureFetch';
const { formatCentsToDollars } = require('../store/calc-helpers');

const MyInfo = () => {
  const { organizationId } = useSelector((state) => state.user);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const toast = useToast();

  // Define field arrays
  // const hiddenFields = [
  //   'id',
  //   'stripe_connected_account_id',
  //   'verified',
  //   'application_id',
  //   'onboarding_form_id',
  // ];

  const shownFields = [
    'name',
    'ein',
    // 'email_verified',
    // 'email',
    'withdrawal_amount',
    'total_donations',
    'total_fees_saved',
    'total_refunds',
    'total_withdrawn',
    'application_status',
  ];

  const editableFields = [
    'website_url',
    'address_line_1',
    'address_line_2',
    'city',
    'state',
    'zip_code',
    'description',
    'phone_number',
  ];

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const response = await secureFetch(
          `/api/organizations/${organizationId}`
        );
        const data = response;
        setOrganization(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching organization:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch organization data',
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
        setLoading(false);
      }
    };

    fetchOrganization();
  }, [organizationId, toast]);

  const handleEditClick = (fieldName) => {
    setEditingField(fieldName);
    setFieldValues({ [fieldName]: organization[fieldName] });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFieldValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleSaveClick = async (fieldName) => {
    try {
      const response = await secureFetch(
        `/api/organizations/${organizationId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          data: { fieldName, value: fieldValues[fieldName] },
        }
      );

      if (response.ok) {
        setOrganization((prevOrg) => ({
          ...prevOrg,
          [fieldName]: fieldValues[fieldName],
        }));
        toast({
          title: 'Success',
          description: `${formatFieldName(fieldName)} updated successfully`,
          status: 'success',
          duration: 9000,
          isClosable: true,
        });
      } else {
        throw new Error(`Failed to update ${fieldName}`);
      }
    } catch (error) {
      console.error(`Error updating ${fieldName}:`, error);
      toast({
        title: 'Error',
        description: `Failed to update ${formatFieldName(fieldName)}`,
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setEditingField(null);
    }
  };

  const handleCancelClick = () => {
    setEditingField(null);
    setFieldValues({});
  };

  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (loading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  if (!organization) {
    return <Box>No organization information available.</Box>;
  }

  const campaignImage = `https://${window.location.host}/images/share-image.png`;

  return (
    <Box p={5} maxW="800px" mx="auto">
      <Helmet>
        <title>My Profile - PurelyGive</title>
        <meta
          name="description"
          content="Update your personal information and account settings on PurelyGive. Customize your profile to represent your organization."
        />
        <meta
          name="keywords"
          content="profile, account settings, personal information, organization details, PurelyGive"
        />
        <meta property="og:title" content="Your Profile Settings - PurelyGive" />
        <meta
          property="og:description"
          content="Manage your account information and personalize your PurelyGive profile for better engagement."
        />
        <meta property="og:image" content={campaignImage} />
        <meta property="og:url" content="https://purelygive.com/profile" />
      </Helmet>
      <Heading mb={5}>My Info</Heading>

      {/* Read-Only Fields */}
      <Text mb={3} fontSize="2xl">
        Organization Details
      </Text>
      <Grid templateColumns="repeat(2, 1fr)" gap={6}>
        {shownFields.map((key) => (
          <GridItem colSpan={[2, 1]} key={key}>
            <FormControl mb={4}>
              <FormLabel>{formatFieldName(key)}</FormLabel>
              <Flex alignItems="center">
                {key === 'email_verified' ? (
                  <Flex alignItems="center">
                    {organization[key] ? (
                      <Badge colorScheme="green">Verified</Badge>
                    ) : (
                      <Badge colorScheme="red">Not Verified</Badge>
                    )}
                  </Flex>
                ) : (
                  <>
                    {['withdrawal_amount', 'total_donations', 'total_fees_saved'].includes(key) ? (
                      <Text flex="1">{organization[key] ? `$${formatCentsToDollars(organization[key])}` : 'N/A'}</Text>
                    ) : (
                      <Text flex="1">{organization[key] || 'N/A'}</Text>
                    )}
                  </>
                )}
              </Flex>
            </FormControl>
          </GridItem>
        ))}
      </Grid>

      {/* Editable Fields */}
      {/* <Text mb={3} fontSize="2xl">
        Editable Information
      </Text> */}
      <Grid templateColumns="repeat(2, 1fr)" gap={6}>
        {editableFields.map((key) => (
          <GridItem colSpan={[2, 1]} key={key}>
            <FormControl mb={4}>
              <FormLabel>{formatFieldName(key)}</FormLabel>
              <Flex alignItems="center">
                {editingField === key ? (
                  <>
                    {key === 'description' ? (
                      <Textarea
                        name={key}
                        value={fieldValues[key]}
                        onChange={handleInputChange}
                        width="100%"
                      />
                    ) : (
                      <Input
                        name={key}
                        value={fieldValues[key]}
                        onChange={handleInputChange}
                        width="100%"
                      />
                    )}
                    <IconButton
                      icon={<CheckIcon />}
                      onClick={() => handleSaveClick(key)}
                      colorScheme="teal"
                      size="sm"
                      ml={2}
                    />
                    <IconButton
                      icon={<CloseIcon />}
                      onClick={handleCancelClick}
                      colorScheme="red"
                      size="sm"
                      ml={2}
                    />
                  </>
                ) : (
                  <>
                    <Text flex="1">{organization[key] || 'N/A'}</Text>
                    <IconButton
                      icon={<EditIcon />}
                      onClick={() => handleEditClick(key)}
                      colorScheme="teal"
                      size="sm"
                      variant="ghost"
                      ml={2}
                    />
                  </>
                )}
              </Flex>
            </FormControl>
          </GridItem>
        ))}
      </Grid>
    </Box >
  );
};

export default MyInfo;
